// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InputRange from 'react-input-range';
import styled, { css } from 'styled-components';

import Formatter from '../../i18n/formatter';
import { __, getCurrency } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { type Licence, Licences } from '../../types/common';
import HistoryItemCashoutStake from '../HistoryItemCashoutStake';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel/InfosLabel';

const inputRangeCss = css`
  .input-range {
    box-sizing: border-box;
    padding: 1.3em 1.5em 1.3em 1em;
    position: relative;
    width: 100%;
  }
  .input-range__label {
    display: none;
  }
  .input-range__track--background {
    width: 100%;
    position: absolute;
  }
  .input-range__track {
    background-color: #eee;
    border-radius: 2px;
    cursor: pointer;
    display: block;
    height: 1px;
    position: relative;
  }
  .input-range__track--active {
    background: ${({ theme }) => theme.common.colors.secondary};
  }
  .input-range--disabled {
    opacity: 0.5;
  }
  .input-range__slider-container {
    left: 0;
  }
  .input-range__slider {
    appearance: none;
    background: ${theme.History.betslip.actions.cashoutButtonBackgroundColor};
    border-radius: 100%;
    cursor: pointer;
    display: block;
    height: 1em;
    width: 1em;
    outline: none;
    position: absolute;
    transform: translate3d(-50%, -50%, 0);
    border: 0.15em solid ${theme.History.betslip.backgroundColor};
  }
`;

const Wrapper = styled.div`
  padding: 0 1em;
  ${/* sc-declaration */ inputRangeCss};
`;

const SpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MIN_INPUT_WIDTH = 7;
const WIDTH_PER_CHAR = 1.2;
const MARGIN = 5;

const StyledCashoutStake = styled(HistoryItemCashoutStake)`
  width: ${({ $nbDigit }) => Math.max(MIN_INPUT_WIDTH, ($nbDigit || 1) * WIDTH_PER_CHAR + MARGIN)}em;
  box-sizing: border-box;
  outline: none;
  text-align: right;
  font-weight: bold;

  :disabled {
    opacity: 0.5;
  }
  :focus {
    border: 0.1em solid #2196f3;
  }
`;

const StyledInfosLabel = styled(InfosLabel)`
  font-size: 0.8em;
`;

const normalizeInputValue = (prevValue, value) => {
  // limits the maximum decimals to 2
  value = value.match(/[0-9]+(\.([0-9]{0,2}))?/gi);

  if (value) {
    if (isNaN(value[0])) {
      return prevValue;
    }

    return value[0];
  }

  return '';
};

type PropTypes = {
  cashoutValue: number;
  cashoutStatus: string;
  licence: Licence;
  cashoutAmount: number;
  onCashoutChange: (...params: unknown) => unknown;
};

const HistoryItemCashoutSelector = ({
  cashoutValue = 0,
  cashoutStatus,
  licence,
  cashoutAmount,
  onCashoutChange,
}: PropTypes) => {
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    onCashoutChange((prevCashoutAmount) => {
      if (dirty) {
        return Math.max(0, Math.min(cashoutValue, prevCashoutAmount));
      }
      return cashoutValue;
    });
  }, [cashoutValue, dirty, onCashoutChange]);

  const onCashoutAmountChange = useCallback(
    (cashoutAmount) => {
      onCashoutChange(Math.round(cashoutAmount * 100) / 100 / 100);
      setDirty(true);
    },
    [onCashoutChange],
  );

  const inputChange = useCallback(
    (value) => {
      onCashoutChange((prevCashoutAmount) => {
        const normalizedValue = normalizeInputValue(prevCashoutAmount, value);
        if (normalizedValue > cashoutValue) {
          return cashoutValue;
        }
        return normalizedValue;
      });
    },
    [cashoutValue, onCashoutChange],
  );

  const cashoutValueLabel = useMemo(() => Formatter.formatNumber(+cashoutValue, getCurrency()), [cashoutValue]);

  const cashoutPlaceHolderLabel = useMemo(() => {
    if (cashoutStatus !== false && cashoutStatus !== true) {
      return __('Cashout (max %1$s)', [cashoutValueLabel]);
    }
  }, [cashoutStatus, cashoutValueLabel]);

  if (!isNaN(cashoutValue) && cashoutValue > 0) {
    return (
      <Wrapper>
        {licence === Licences.DE ? (
          <StyledInfosLabel
            type={InfosLabelTypes.INFOS_YELLOW}
            symbol={'!'}
            label={__('!desc:CashoutPendingRegulation')}
          />
        ) : null}
        <SpaceBetween>
          <InputRange
            formatLabel={(v) => Formatter.formatNumber(v, getCurrency())}
            step={0.01 * 100}
            maxValue={cashoutValue * 100}
            minValue={0}
            value={cashoutAmount * 100}
            onChange={onCashoutAmountChange}
            disabled={cashoutStatus !== null && cashoutStatus !== undefined}
          />
          <StyledCashoutStake
            $nbDigit={`${cashoutValue * 100}`.length}
            disabled={cashoutStatus !== null && cashoutStatus !== undefined}
            stake={cashoutAmount?.toString() || ''} // cashoutAmount is a number and we need to convert it to a string
            onStakeChange={inputChange}
            placeholderLabel={cashoutPlaceHolderLabel}
          />
        </SpaceBetween>
      </Wrapper>
    );
  }
  return <Wrapper />;
};

export default HistoryItemCashoutSelector;
